/**
 * Perfis de acesso: tipos e regras puras de visibilidade.
 *
 * O backend (`@mpf-permissoes`) já devolve a regra resolvida: Manager tem
 * `is_manager` e tudo `null`; nas dimensões restritivas `null` quer dizer "sem
 * restrição" e uma lista é a allowlist. A UI só esconde — quem garante é o
 * backend.
 */

export type DimensaoRestritiva =
  | 'toolbar'
  | 'layouts'
  | 'types'
  | 'blocks'
  | 'fields';

export const DIMENSOES_RESTRITIVAS: DimensaoRestritiva[] = [
  'toolbar',
  'types',
  'layouts',
  'blocks',
  'fields',
];

export type MpfPermissoes = {
  is_manager: boolean;
  perfis: string[];
  controlpanels: string[] | null;
  /** Revisa/edita notícias (grupos ou papéis do fluxo editorial). */
  participa_fluxo_editorial?: boolean;
  /**
   * Pode abrir a paleta de estilos do bloco no editor (permissão `Edit block
   * styles`: administradores e perfis que a receberam). AB#11159.
   */
  block_styles?: boolean;
} & Record<DimensaoRestritiva, string[] | null>;

export type MpfPrincipal = {
  id: string;
  type: 'user' | 'group';
  title: string;
};

export type MpfPerfil = {
  '@id'?: string;
  id: string;
  role_id?: string;
  title: string;
  description: string;
  controlpanels: string[];
  extras: string[];
  principals: MpfPrincipal[];
} & Record<DimensaoRestritiva, string[] | null>;

const liberado = (
  permissoes: MpfPermissoes | null | undefined,
  dimensao: DimensaoRestritiva,
  item: string,
) => {
  // Sem dados (ainda carregando, anônimo) a UI não restringe: o backend barra.
  if (!permissoes || permissoes.is_manager) return true;
  const lista = permissoes[dimensao];
  return lista == null || lista.includes(item);
};

export const podeVerToolbar = (
  permissoes: MpfPermissoes | null | undefined,
  item: string,
) => liberado(permissoes, 'toolbar', item);

export const blocoPermitido = (
  permissoes: MpfPermissoes | null | undefined,
  blocoId: string,
) => liberado(permissoes, 'blocks', blocoId);

export const layoutPermitido = (
  permissoes: MpfPermissoes | null | undefined,
  layout: string,
) => liberado(permissoes, 'layouts', layout);

/**
 * Tiles do Site Setup que não vêm do `@controlpanels` (usuários, grupos,
 * regras...). Aqui a regra é a oposta: sem dados, não mostra — o tile só
 * aparece quando o backend confirma o acesso ao configlet.
 */
export const podeVerControlpanel = (
  permissoes: MpfPermissoes | null | undefined,
  configletId: string,
) => {
  if (!permissoes) return false;
  if (permissoes.is_manager) return true;
  return (permissoes.controlpanels ?? []).includes(configletId);
};

/**
 * Paleta de estilos do bloco (volto-block-style). Ao contrário da toolbar,
 * sem dados NÃO mostra: a decisão editorial é que o usuário comum não estiliza
 * (AB#11159), e um backend sem a chave é um backend anterior à regra.
 */
export const podeEstilizarBlocos = (
  permissoes: MpfPermissoes | null | undefined,
) =>
  !!permissoes && (permissoes.is_manager || permissoes.block_styles === true);

/** Mensagem legível de um erro do middleware de API do Volto. */
export const mensagemDeErro = (erro: any): string | null => {
  if (!erro) return null;
  const corpo = erro.response?.body;
  return corpo?.message || corpo?.error?.message || erro.message || null;
};

/**
 * A Toolbar aparece para quem lista conteúdos na página atual — e também para
 * quem trabalha no fluxo editorial, que tem papel só dentro das notícias da
 * unidade e ficaria sem a barra (e sem o sino) no resto do site.
 */
export const mostraToolbar = (
  permissoes: MpfPermissoes | null | undefined,
  temAcaoConteudos: boolean,
) => temAcaoConteudos || !!permissoes?.participa_fluxo_editorial;
